import { EnvConfig } from '@config';
import { Feature } from 'constants/features';
import { getMaxPointsFromInterval } from 'constants/graphs';
import { Mnemonic } from 'constants/mnemonics';
import { TargetVar } from 'constants/targetVars';
import { useEffectOnUnitSystemChange } from 'hooks/useEffectOnUnitSystemChange';
import queryString from 'query-string';
import { useCallback } from 'react';
import {
  RopDepthDataDTO,
  RopOptimizationHistoryDTO,
  RopOptimizationMetadataDto,
  RopOptimizationQatarDTO,
  RopOptimizationUKDTO,
  RopQatarOffsetWellboresDTO,
} from 'types/RopOptimization.dto';
import { AllAlarmsProportionDTO } from 'types/alarm';
import {
  FlowBackFingerprintingDTO,
  KickDetectionRigStateDTO,
  MainRiskDTO,
  PredictionRigStateDTO,
  SingleValueGraphDTO,
  TimeseriesGraphDTO,
  VirtualSensorGraphDTO,
} from 'types/graph.dto';
import { MnemonicsMappingAndLiveStatusDTO } from 'types/mnemonicsMapping';
import { Scope } from 'types/scope';
import { UnitSystems } from 'types/unitSystem';
import { WebIncident } from 'types/webIncident';

import { useFetch, UseFetchOptions } from './useFetch';

const MNEMONIC_LIVE_PERIOD_IN_HOURS = 5;
const kickStuckApiBaseUrl = EnvConfig.drillx_so_api_url || '';

const useFetchKickStuckApiEndpoint = <ResponseType>(
  endpoint: string,
  options: UseFetchOptions<ResponseType> = {},
) => useFetch<ResponseType>(kickStuckApiBaseUrl + endpoint, options);

export const useFetchIndexedTimeseries = <
  IndexedTimeseriesDTO extends Record<string, TimeseriesGraphDTO>,
>(
  endpoint: string,
) => {
  const { responseBody, isLoading, error, doFetch } =
    useFetchKickStuckApiEndpoint<IndexedTimeseriesDTO>(endpoint);

  const enhancedDoFetch = useCallback(
    (startDatetime: string, endDatetime: string, maxPoints?: number) => {
      return doFetch({ queryParams: { startDatetime, endDatetime, maxPoints } });
    },
    [doFetch],
  );

  return {
    doFetchIndexedTimeseries: enhancedDoFetch,
    indexedTimeseries: responseBody,
    isIndexedTimeseriesLoading: isLoading,
    indexedTimeseriesLoadingError: error,
  };
};

export const useAutoFetchGraphData = <M extends Mnemonic, F extends Feature, T extends TargetVar>(
  wellboreId: string | undefined,
  mnemonics: Readonly<M[]>,
  featureNames: Readonly<F[]>,
  targetVarNames: Readonly<T[]>,
  startDatetime: string,
  endDatetime: string,
  maxPoints: number,
  unitSystem: UnitSystems | undefined,
  stateId?: string,
) => {
  const queryParams = {
    startDatetime,
    endDatetime,
    maxPoints,
    mnemonics,
    featureNames,
    targetVarNames,
    stateId,
  };
  const queryStringParams = queryString.stringify(queryParams, { arrayFormat: 'bracket' });

  return useFetchKickStuckApiEndpoint<
    Record<M | F, TimeseriesGraphDTO> & Record<T, VirtualSensorGraphDTO>
  >(`/graphs/${wellboreId}?${queryStringParams}`, {
    autoFetch: wellboreId !== undefined && unitSystem !== undefined && [unitSystem],
    resetResponseDependencyList: [wellboreId],
  });
};

export const useFetchGraphData = <M extends Mnemonic, F extends Feature, T extends TargetVar>(
  wellboreId: string,
  mnemonics: Readonly<M[]>,
  featureNames?: Readonly<F[]>,
  targetVarNames?: Readonly<T[]>,
  stateId?: string,
) => {
  const { doFetch, responseBody, isLoading, error } = useFetchKickStuckApiEndpoint<
    Record<M | F, TimeseriesGraphDTO> & Record<T, VirtualSensorGraphDTO>
  >(`/graphs/${wellboreId}`, { resetResponseDependencyList: [wellboreId] });

  const enhancedDoFetch = useCallback(
    (startDatetime: string, endDatetime: string, maxPoints: number) => {
      const baseQueryParams = { mnemonics, featureNames, targetVarNames, stateId };

      return doFetch({
        queryParams: { ...baseQueryParams, startDatetime, endDatetime, maxPoints },
      });
    },
    [doFetch, featureNames, mnemonics, stateId, targetVarNames],
  );

  return {
    doFetchGraphData: enhancedDoFetch,
    graphData: responseBody,
    isGraphDataLoading: isLoading,
    graphDataLoadingError: error,
  };
};

export const useFetchAllMnemonicsMapping = () => {
  const startDate = new Date();
  const endDate = new Date();
  startDate.setHours(startDate.getHours() - MNEMONIC_LIVE_PERIOD_IN_HOURS);

  return useFetchKickStuckApiEndpoint<Record<string, MnemonicsMappingAndLiveStatusDTO>>(
    `/preprocessed/live-mnemonics?startTime=${startDate.toISOString()}&endTime=${endDate.toISOString()}`,
  );
};

export const useFetchWellboreMnemonicsMapping = (wellboreId: string) => {
  const {
    responseBody,
    isLoading,
    doFetch: initialDoFetch,
  } = useFetchKickStuckApiEndpoint<MnemonicsMappingAndLiveStatusDTO>(
    `/preprocessed/live-mnemonics/${wellboreId}`,
  );

  const doFetch = useCallback(() => {
    const startDate = new Date();
    const endDate = new Date();
    startDate.setHours(startDate.getHours() - MNEMONIC_LIVE_PERIOD_IN_HOURS);

    initialDoFetch({
      queryParams: { startTime: startDate.toISOString(), endTime: endDate.toISOString() },
    });
  }, [initialDoFetch]);

  return {
    responseBody,
    isLoading,
    doFetch,
  };
};

export const useFetchMainRisk = (
  scope: Scope.KICK_PREDICTION_SCOPE | Scope.STUCK_PREDICTION_SCOPE,
  wellboreId?: string,
) => {
  const { responseBody, isLoading, error, doFetch } = useFetchKickStuckApiEndpoint<MainRiskDTO>(
    `/main-risk/${scope}/${wellboreId}`,
  );

  const enhancedDoFetch = useCallback(
    (startDatetime: string, endDatetime: string) => {
      return doFetch({ queryParams: { startDatetime, endDatetime } });
    },
    [doFetch],
  );

  return {
    doFetchMainRisk: enhancedDoFetch,
    mainRisk: responseBody,
    isMainRiskLoading: isLoading,
    mainRiskLoadingError: error,
  };
};

export const useFetchPredictionRigState = (wellboreId: string) => {
  return useFetchKickStuckApiEndpoint<PredictionRigStateDTO>(
    `/wellbores/${wellboreId}/prediction/graphs/rig-state`,
  );
};

export const useFetchKickDetectionRigState = (wellboreId: string) => {
  return useFetchKickStuckApiEndpoint<KickDetectionRigStateDTO>(
    `/wellbores/${wellboreId}/detection/graphs/rig-state`,
  );
};

export const useFetchFlowBackFingerprinting = (
  wellboreId: string,
  startDatetime: string,
  endDatetime: string,
) => {
  const queryStringParams = queryString.stringify({
    startDatetime,
    endDatetime,
    maxPoints: getMaxPointsFromInterval({ start: startDatetime, end: endDatetime }),
  });

  return useFetchKickStuckApiEndpoint<FlowBackFingerprintingDTO>(
    `/wellbores/${wellboreId}/graphs/flow-back-fingerprinting?${queryStringParams}`,
  );
};

export const useAutoFetchLastDepth = (wellboreId: string | undefined, unitSystem?: UnitSystems) => {
  const queryStringParams = queryString.stringify({ unitSystem });

  return useFetchKickStuckApiEndpoint<SingleValueGraphDTO>(
    `/eddr/wellbores/${wellboreId}/latest-depth?${queryStringParams}`,
    {
      autoFetch: wellboreId !== undefined,
      resetResponseDependencyList: [wellboreId],
    },
  );
};

export const useAutoFetchRopOptimizationUK = (wellboreId: string, isLiveModeOn: boolean) => {
  const { responseBody, isLoading, error, doFetch } =
    useFetchKickStuckApiEndpoint<RopOptimizationUKDTO>(`/rop-optimization-uk/${wellboreId}`, {
      autoFetch: true,
      resetResponseDependencyList: [wellboreId],
    });

  useEffectOnUnitSystemChange(() => {
    doFetch();
  });

  return {
    ropOptimizationUKCurrentData: responseBody,
    isRopUKLoading: isLoading,
    ropUKLoadingError: error,
    doFetchRopOptimizationUK: doFetch,
  };
};

export const useAutoFetchRopOptimizationQatar = (wellboreId: string) => {
  const { doFetch, responseBody, isLoading, error } =
    useFetchKickStuckApiEndpoint<RopOptimizationQatarDTO>(`/rop-optimization-qatar/${wellboreId}`, {
      autoFetch: true,
      resetResponseDependencyList: [wellboreId],
    });

  return {
    ropOptimizationQatarCurrentData: responseBody,
    isRopQatarLoading: isLoading,
    ropQatarLoadingError: error,
    doFetchRopOptimizationQatar: doFetch,
  };
};

export const useAutoFetchRopQatarOffsetWell = (wellboreId: string) => {
  const { doFetch, responseBody, isLoading } =
    useFetchKickStuckApiEndpoint<RopQatarOffsetWellboresDTO>(
      `/rop-optimization-qatar/${wellboreId}/offset-wellbores-rop`,
      {
        autoFetch: true,
        resetResponseDependencyList: [wellboreId],
      },
    );

  return {
    ropQatarOffsetWellCurrentData: responseBody,
    isRopQatarOffsetWellLoading: isLoading,
    doFetchRopQatarOffsetWell: doFetch,
  };
};

export const useAutoFetchRopQatarCurrentWellRop = (wellboreId: string) => {
  const { doFetch, responseBody } = useFetchKickStuckApiEndpoint<RopDepthDataDTO[][]>(
    `/rop-optimization-qatar/${wellboreId}/current-wellbore-rop`,
    {
      autoFetch: true,
      resetResponseDependencyList: [wellboreId],
    },
  );

  return {
    ropQatarCurrentWellRop: responseBody,
    doFetchRopQatarCurrentWellRop: doFetch,
  };
};

export const useFetchRopOptimizationHistory = (wellboreId: string) => {
  return useFetchKickStuckApiEndpoint<RopOptimizationHistoryDTO>(
    `/rop-optimization-uk/rop-history/${wellboreId}`,
    { resetResponseDependencyList: [wellboreId] },
  );
};

export const useAutoFetchFormationNameAndDueDateRopOptimization = (wellboreId: string) => {
  return useFetchKickStuckApiEndpoint<RopOptimizationMetadataDto>(
    `/rop-optimization-uk/rop-metadata/${wellboreId}`,
    {
      autoFetch: true,
      resetResponseDependencyList: [wellboreId],
    },
  );
};

export const useFetchWellboreAlarms = (wellboreId: string, date: string) => {
  const queryStringParams = queryString.stringify({ date });

  return useFetchKickStuckApiEndpoint<AllAlarmsProportionDTO>(
    `/alarms/all/${wellboreId}/percentage?${queryStringParams}`,
    {
      autoFetch: [wellboreId, date],
    },
  );
};

export const useAutoFetchActiveWebIncidents = () => {
  return useFetchKickStuckApiEndpoint<WebIncident[]>(`/web-incidents/active`, {
    autoFetch: true,
  });
};
